import React from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { MenuItem } from '@atlaskit/editor-common/extensions';
import { createQuickInsertMatcher } from '@atlaskit/editor-common/quick-insert/create-quick-insert-matcher';
import { getQuickInsertMenuItemParents } from '@atlaskit/editor-common/quick-insert/get-menu-item-parents';
import { STRUCTURE_SECTION } from '@atlaskit/editor-common/quick-insert/keys';
import { EXTENSION_ITEM_RANK } from '@atlaskit/editor-common/quick-insert/rank';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { ExtensionPlugin } from '@atlaskit/editor-plugins/extension';
import type {
	CommonComponentProps,
	RegisterMenuItem,
} from '@atlaskit/editor-ui-control-model/types';

import { ExtensionQuickInsertMenuItem } from './ExtensionQuickInsertMenuItem';

type ExtensionQuickInsertComponentProps = CommonComponentProps & {
	onInsert?: () => void;
};

const structureExtensionItemKeyRanks: Readonly<Record<string, number>> = {
	'toc:toc': 600,
	'native-tabs:native-tabs': 1900,
	'cards:quick-insert': 2000,
	'carousel:quick-insert': 2100,
	'spotlight:quick-insert': 2200,
	'com.atlassian.linking-platform.create:linking-platform-create-jira-issue': 2500,
	'com.atlassian.linking-platform.create:linking-platform-create-confluence-page': 2600,
	'create-from-template:create-from-template': 2700,
	'anchor:anchor': 2900,
	'smart-button:quick-insert': 3000,
};

const UNRECOGNIZED_STRUCTURE_EXTENSION_ITEM_RANK = 3100;

const getStructureExtensionItemRank = (item: MenuItem): number | undefined =>
	structureExtensionItemKeyRanks[item.key];

const isStructureExtensionItem = (item: MenuItem): boolean =>
	getQuickInsertMenuItemParents({ categories: item.categories, rank: 0 }).some(
		(parent) => parent.key === STRUCTURE_SECTION.key,
	);

export const getExtensionQuickInsertComponents = ({
	apiRef,
	createAnalyticsEvent,
	items,
}: {
	apiRef: React.MutableRefObject<PublicPluginAPI<[ExtensionPlugin]> | undefined>;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	items: MenuItem[];
}): RegisterMenuItem<ExtensionQuickInsertComponentProps>[] => {
	const unrecognizedStructureItemRanks = new Map(
		items
			.filter(isStructureExtensionItem)
			.filter((item) => getStructureExtensionItemRank(item) === undefined)
			.sort(
				(firstItem, secondItem) =>
					firstItem.title.localeCompare(secondItem.title) ||
					firstItem.key.localeCompare(secondItem.key),
			)
			.map((item, index) => [item, UNRECOGNIZED_STRUCTURE_EXTENSION_ITEM_RANK + index]),
	);

	return items.map((item, index) => {
		const structureRank = getStructureExtensionItemRank(item);
		const isStructureItem = structureRank !== undefined;
		const categories = isStructureItem ? ['structure'] : item.categories;
		const fallbackRank =
			unrecognizedStructureItemRanks.get(item) ?? EXTENSION_ITEM_RANK + (item.priority ?? index);

		return {
			type: 'menu-item',
			key: item.key,
			parents: getQuickInsertMenuItemParents({
				categories,
				rank: structureRank ?? fallbackRank,
			}),
			match: createQuickInsertMatcher(() => ({
				description: item.description,
				keywords: item.keywords,
				title: item.title,
			})),
			component: (props) => {
				return (
					<ExtensionQuickInsertMenuItem
						apiRef={apiRef}
						createAnalyticsEvent={createAnalyticsEvent}
						item={item}
						onInsert={props.onInsert}
					/>
				);
			},
		};
	});
};
